import { useState } from 'react';
import {
  Modal,
  TextInput,
  Textarea,
  Button,
  Select,
  Group,
  Stack,
  Text,
  Alert,
  UnstyledButton
} from '@mantine/core';
import { IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { submitFeedback } from '../../services/feedbackService';
import { useAuth } from '../../hooks/useAuth';

type FeedbackType = 'issue' | 'feature_request' | 'general';

type FeedbackModalProps = {
  opened: boolean;
  onClose: () => void;
};

export const FeedbackModal = ({ opened, onClose }: FeedbackModalProps) => {
  const { user } = useAuth();
  const [type, setType] = useState<FeedbackType>('general');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const feedbackTypeOptions = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'issue', label: 'Report an Issue' },
    { value: 'feature_request', label: 'Feature Request' },
  ];

  const resetForm = () => {
    setType('general');
    setSubject('');
    setMessage('');
    setSubmitStatus(null);
    setErrorMessage('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!subject.trim() || !message.trim()) {
      setSubmitStatus('error');
      setErrorMessage('Please fill in both subject and message fields.');
      return;
    }

    if (!user) {
      setSubmitStatus('error');
      setErrorMessage('You must be logged in to send a message.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      await user.getIdToken().then(token =>
        submitFeedback({
          type,
          subject: subject.trim(),
          message: message.trim(),
        }, token)
      );

      setSubmitStatus('success');
      handleClose();
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to send message');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={<Text fw={600} size="lg">Contact us</Text>}
      size="md"
      centered
      radius="lg"
    >
      <Stack gap="md">
        {submitStatus === 'success' && (
          <Alert icon={<IconCheck size={16} />} color="green" title="Success!" radius="lg">
            Your message has been submitted successfully. Thank you for contacting us!
          </Alert>
        )}

        {submitStatus === 'error' && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error" radius="lg">
            {errorMessage}
          </Alert>
        )}

        <Select
          label="Message Type"
          placeholder="Select message type"
          data={feedbackTypeOptions}
          value={type}
          onChange={(value) => setType(value as FeedbackType)}
          required
        />

        <TextInput
          label="Subject"
          placeholder="Brief description of your message"
          value={subject}
          onChange={(event) => setSubject(event.currentTarget.value)}
          required
          maxLength={200}
        />

        <Textarea
          label="Message"
          placeholder="Please provide details about your inquiry..."
          value={message}
          onChange={(event) => setMessage(event.currentTarget.value)}
          required
          minRows={4}
          maxRows={8}
          autosize
          maxLength={2000}
        />

        <Text size="xs" c="dimmed">
          We appreciate you taking the time to contact us. Your input helps us improve Meals You Love!
        </Text>

        <Group justify="flex-end" mt="md" gap="lg">
          <UnstyledButton onClick={handleClose} disabled={isSubmitting}>
            <Text size="sm" c="dimmed">Cancel</Text>
          </UnstyledButton>
          <Button
            onClick={handleSubmit}
            loading={isSubmitting}
          >
            Send message
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};